import { morph } from '@theme/morph';
const cache = new Map();
let pending;
let generation = 0;
history.replaceState({ ...history.state, evaColourSwap: true }, '', location.href);

function load(url, signal) {
  if (cache.has(url)) return Promise.resolve(cache.get(url));
  return fetch(url, { signal }).then(response => {
    if (!response.ok) throw new Error('Product unavailable');
    return response.text();
  }).then(html => {
    cache.set(url, html);
    if (cache.size > 12) cache.delete(cache.keys().next().value);
    return html;
  });
}

async function swap(url, push = true) {
  const token = ++generation;
  pending?.abort();
  pending = new AbortController();
  const section = document.querySelector('.eva-colour-choice')?.closest('.shopify-section');
  if (!section) return;
  section.setAttribute('aria-busy', 'true');
  try {
    const html = await load(url, pending.signal);
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('.eva-colour-choice')?.closest('.shopify-section');
    if (!next) throw new Error('Missing product section');
    if (token !== generation) return;
    const desktop = matchMedia('(min-width: 990px)').matches;
    const images = Array.from(next.querySelectorAll(desktop ? '.ea-ps__slide .ea-ps__img' : '.ea-ps__media .swiper-wrapper img')).slice(0, 2);
    await Promise.all(images.map(img => {
      const preload = new Image();
      const source = Array.from(img.closest('picture')?.querySelectorAll('source') || []).find(item => !item.media || matchMedia(item.media).matches);
      preload.sizes = source?.sizes || img.sizes || '100vw';
      const srcset = source?.srcset || img.srcset || img.dataset.srcset;
      if (srcset) preload.srcset = srcset;
      preload.src = img.dataset.src || img.src || '';
      return preload.decode().catch(() => {});
    }));
    if (token !== generation) return;
    const y = window.scrollY;
    if (push) history.pushState({ ...history.state, evaColourSwap: true }, '', url);
    document.title = doc.title;
    const picker = section.querySelector('eva-bundle-picker');
    const newPicker = next.querySelector('eva-bundle-picker');
    const currentId = newPicker?.dataset.currentId;
    const update = () => {
      for (const selector of ['.eva-pheader', '.eva-colour-choice', 'product-form-component', 'ea-preorder-sync', 'sticky-add-to-cart']) {
        const existing = section.querySelector(selector);
        const replacement = next.querySelector(selector);
        if (!existing || !replacement) continue;
        morph(existing, replacement);
        for (const attribute of Array.from(replacement.attributes)) {
          if (attribute.name.startsWith('data-')) existing.setAttribute(attribute.name, attribute.value);
        }
        existing.updatedCallback?.();
      }
      for (const selector of ['.ea-ps__media', 'ea-showcase-slider']) {
        const existing = section.querySelector(selector);
        const replacement = next.querySelector(selector);
        if (existing && replacement) existing.replaceWith(replacement);
      }
      if (picker && currentId) {
        picker.dataset.currentId = currentId;
        picker.currentIndex = picker.colours.findIndex(colour => String(colour.id) === currentId);
        if (picker.currentIndex >= 0) picker.slotColour[0] = picker.currentIndex;
        picker.bindForm();
        picker.render();
      }
      section.querySelectorAll('ea-atc-bundle-sync').forEach(sync => sync.boot?.());
      window.scrollTo({ top: y, behavior: 'instant' });
    };
    if (document.startViewTransition && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
      await document.startViewTransition(update).updateCallbackDone;
    } else update();
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', doc.querySelector('link[rel="canonical"]')?.href || url);
    requestAnimationFrame(() => {
      window.scrollTo({ top: y, behavior: 'instant' });
      section.querySelector('.eva-swatch.is-active')?.focus({ preventScroll: true });
    });
  } catch (error) {
    if (error.name !== 'AbortError' && token === generation) {
      console.warn('EVA colour update failed; keeping the current product.', error);
    }
  } finally {
    if (token === generation) section.removeAttribute('aria-busy');
  }
}

window.addEventListener('click', event => {
  const link = event.target.closest('.eva-colour-choice a.eva-swatch');
  if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const url = new URL(link.href, location.href);
  if (url.origin !== location.origin) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  if (url.pathname === location.pathname) return;
  swap(url.href);
}, { capture: true });

document.addEventListener('pointerover', event => {
  const link = event.target.closest('.eva-colour-choice a.eva-swatch');
  if (link && new URL(link.href).origin === location.origin) load(link.href).catch(() => {});
}, { passive: true });

window.addEventListener('popstate', event => {
  if (event.state?.evaColourSwap) swap(location.href, false);
});
